import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { getMasteryBand } from './masteryBands';
interface SubjectModuleRowProps {
  variant: 'parent' | 'teacher';
  moduleId: string;
  moduleTitle: string;
  moduleIcon: React.ReactNode;
  mastery: number;
  trend: 'up' | 'down' | 'stable';
  trendDelta?: number;
  distribution?: {
    strong: number;
    solid: number;
    developing: number;
    attention: number;
    total: number;
  };
  practicedCount?: number;
  parentSubtitle?: string;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}
export function SubjectModuleRow({
  variant,
  moduleId,
  moduleTitle,
  moduleIcon,
  mastery,
  trend,
  trendDelta,
  distribution,
  practicedCount,
  parentSubtitle,
  isExpanded,
  onToggle,
  children
}: SubjectModuleRowProps) {
  const band = getMasteryBand(mastery);
  return <div className="bg-white border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-id="element-9200" data-mp="3tyhxjvvl9vw3">
      <div className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4 cursor-pointer" onClick={onToggle} role="button" tabIndex={0} aria-expanded={isExpanded} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle();
      }
    }} data-id="element-9201" data-mp="1juxynyjl9heh">
        
        {/* Icon */}
        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${band.bgClass} ${band.textClass}`} data-id="element-9202" data-mp="112dm5ro75owu">
          
          {moduleIcon}
        </div>

        {/* Title & Subtitle */}
        <div className="flex-1 min-w-0" data-id="element-9203" data-mp="1a2jz9cfzgmvh">
          <div className="font-bold text-slate-900 truncate" data-id="element-9204" data-mp="1a0pa1lxoi8ll">{moduleTitle}</div>
          <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-9205" data-mp="1ev3a41nad3wl">
            {variant === 'parent' ? <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5" data-id="element-9206" data-mp="hdcvnz1o973d">
                <span className={`font-bold ${band.textClass}`} data-id="element-9207" data-mp="mc60z46t1rsg">
                  {band.label}
                </span>
                <span className="text-slate-300" data-id="element-9208" data-mp="1pmk6sm45mxr2">·</span>
                <span data-id="element-9209" data-mp="1kpabwqxzxtvu">{mastery}%</span>
                {parentSubtitle && <>
                    <span className="text-slate-300" data-id="element-9210" data-mp="1ygfd3xh0iu8a">·</span>
                    <span className="text-slate-400" data-id="element-9211" data-mp="1yfx8odk759pa">{parentSubtitle}</span>
                  </>}
              </span> : <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5" data-id="element-9212" data-mp="1ufdbj9zmi635">
                <span data-id="element-9213" data-mp="16q26vhf5jrjq">{practicedCount || 0} leerlingen</span>
                <span className="text-slate-300" data-id="element-9214" data-mp="11qfsv5ri279v">·</span>
                <span className={`font-bold ${band.textClass}`} data-id="element-9215" data-mp="11s0okypjvw9b">
                  {mastery}% gemiddeld
                </span>
              </span>}
          </div>
        </div>

        {/* Progress / Distribution */}
        <div className="w-20 sm:w-28 md:w-32 flex-shrink-0 flex items-center justify-end" data-id="element-9216" data-mp="1asyvppvacjw4">
          {variant === 'parent' ? <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-9217" data-mp="2m61com1arbgt">
              <motion.div initial={{
            width: 0
          }} animate={{
            width: `${mastery}%`
          }} className={`h-full rounded-full ${band.barClass}`} data-id="element-9218" data-mp="2m842b4yjf2l0" />
            
            </div> : distribution && distribution.total > 0 ? <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden flex" data-id="element-9219" data-mp="2kpnhxlpovq94">
              {distribution.strong > 0 && <div className="h-full bg-emerald-500" style={{
            width: `${distribution.strong / distribution.total * 100}%`
          }} title={`${distribution.strong} sterk`} data-id="element-9220" data-mp="2pp02u0x02n92" />}
              {distribution.solid > 0 && <div className="h-full bg-indigo-500" style={{
            width: `${distribution.solid / distribution.total * 100}%`
          }} title={`${distribution.solid} goed op weg`} data-id="element-9221" data-mp="3ic31ct1oexzb" />}
              {distribution.developing > 0 && <div className="h-full bg-amber-500" style={{
            width: `${distribution.developing / distribution.total * 100}%`
          }} title={`${distribution.developing} aan het groeien`} data-id="element-9222" data-mp="38kqaa3npcbuc" />}
              {distribution.attention > 0 && <div className="h-full bg-rose-500" style={{
            width: `${distribution.attention / distribution.total * 100}%`
          }} title={`${distribution.attention} aandacht nodig`} data-id="element-9223" data-mp="2chsusgcczrfp" />}
            </div> : <div className="w-16 h-2 bg-slate-100 rounded-full" data-id="element-9224" data-mp="23ftc7jlp4fan" />}
        </div>

        {/* Trend */}
        <div className="hidden sm:flex w-16 flex-shrink-0 justify-end" data-id="element-9225" data-mp="1yibd0kxhdefu">
          {trend === 'up' && <span className="flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded uppercase tracking-wider" data-id="element-9226" data-mp="2w5jqptr8pj7a">
              <ArrowUpRight className="w-3 h-3" data-id="element-9227" data-mp="2r8a2ghfl7dl1" />
              {trendDelta && trendDelta > 0 ? `+${trendDelta}%` : ''}
            </span>}
          {trend === 'down' && <span className="flex items-center gap-0.5 text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded uppercase tracking-wider" data-id="element-9228" data-mp="2hfcevwvqmyfk">
              <ArrowDownRight className="w-3 h-3" data-id="element-9229" data-mp="3a51kyonb1oo1" />
              {trendDelta && trendDelta < 0 ? `${trendDelta}%` : ''}
            </span>}
        </div>

        {/* Chevron */}
        <div className="w-6 flex-shrink-0 flex justify-end text-slate-400" data-id="element-9230" data-mp="3hi0vlpqy6xjb">
          {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-9231" data-mp="3mfj45f11zaqs" /> : <ChevronDown className="w-5 h-5" data-id="element-9232" data-mp="3redlecjdgr8s" />}
        </div>
      </div>

      <AnimatePresence data-id="element-9233" data-mp="3rd2kckhb9xld">
        {isExpanded && children && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden bg-slate-50/50 border-t border-slate-100" data-id="element-9234" data-mp="8ci4q5nf1xp">
          
            {children}
          </motion.div>}
      </AnimatePresence>
    </div>;
}